// src/views/admin/main/others/messages/components/Chat.jsx

import React from 'react';
import { FaUsers, FaImage, FaFileAlt, FaCube } from "react-icons/fa";
import md5 from 'md5';

// Utility function to get Gravatar URL
const getGravatarUrl = (email) => {
    if (!email) return 'https://www.gravatar.com/avatar/?d=mp';
    const hash = md5(email.trim().toLowerCase());
    return `https://www.gravatar.com/avatar/${hash}?d=identicon`;
};

// Utility function to construct proper image URL
const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith("http")) {
        return imageUrl;
    }
    
    const serverUrl = process.env.REACT_APP_API_BASE_URL;
    if (serverUrl) {
        return `${serverUrl.replace(/\/$/, "")}/${imageUrl.replace(/^\/uploads/, "uploads/images")}`;
    }
    
    return imageUrl;
};

const Chat = ({ chatData, isSelected }) => {
    const { type, participant, team, lastMessage, unreadCount, isMentorChat, currentUserId } = chatData;

    const name = type === 'individual' ? `${participant?.firstName || ''} ${participant?.lastName || ''}`.trim() : team?.name;
    const photo = type === 'individual' ? (getImageUrl(participant?.imageUrl) || getGravatarUrl(participant?.email)) : null;
    const isOnline = participant?.status === 'Online';
    
    const time = lastMessage?.timestamp
        ? new Date(lastMessage.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
        
    let messageSnippet;
    const prefix = lastMessage?.sender?.id === currentUserId ? 'You: ' : '';

    if (lastMessage?.content) {
        messageSnippet = prefix + lastMessage.content;
    } else if (lastMessage?.fileType) {
        switch (lastMessage.fileType) {
            case 'image':
                messageSnippet = prefix + 'Sent an image';
                break;
            case 'document':
                messageSnippet = prefix + 'Sent a document';
                break;
            case 'model':
                messageSnippet = prefix + 'Sent a 3D model';
                break;
            default:
                messageSnippet = prefix + 'Sent a file';
                break;
        }
    } else {
        messageSnippet = (type === 'individual' ? 'No messages yet' : 'Team chat created');
    }

    return (
        <div className={`flex w-full cursor-pointer items-center gap-4 rounded-2xl p-3 transition-all duration-300 ease-in-out
            ${isSelected 
                ? "bg-brand-500 shadow-lg" 
                : "bg-gray-100 dark:bg-navy-700 hover:bg-orange-300 dark:hover:bg-navy-600 hover:shadow-md"
            }`}
        >
            {/* Avatar Section */}
            <div className="relative h-14 w-14 flex-shrink-0">
                {photo ? (
                    <img 
                        className="h-full w-full rounded-full object-cover" 
                        src={photo} 
                        alt={name}
                        onError={(e) => {
                            e.target.onerror = null; // Prevent infinite loop
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div className={`flex h-full w-full items-center justify-center rounded-full ${isSelected ? 'bg-white/20' : 'bg-gray-200 dark:bg-navy-800'} ${photo ? 'hidden' : ''}`}>
                    <FaUsers className={`h-6 w-6 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                </div>
                {isOnline && <div className={`absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 bg-green-500 ${isSelected ? 'border-brand-500' : 'border-white dark:border-navy-700'}`} />}
            </div>

            {/* Info Section */}
            <div className="w-full overflow-hidden">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h1 className={`truncate text-lg font-bold ${isSelected ? 'text-white' : 'text-navy-700 dark:text-white'}`}>
                            {name}
                        </h1>
                        {isMentorChat && (
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${isSelected ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-500 dark:bg-orange-500/20'}`}>
                                Mentor
                            </span>
                        )}
                    </div>
                    <p className={`text-xs font-medium ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                        {time}
                    </p>
                </div>
                <div className="flex items-center justify-between">
                    <p className={`truncate text-sm ${isSelected ? 'text-white/90' : 'font-normal text-gray-600 dark:text-gray-300'}`}>
                        {messageSnippet}
                    </p>
                    {unreadCount > 0 && !isSelected && (
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-500 text-xs font-bold text-white">
                            {unreadCount}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Chat;